import { ORIGIN_RISE } from "../combat/shapes.ts";
import { inSight } from "../combat/sight.ts";
import type { CollisionGrid } from "../movement/step.ts";

/**
 * Whether a viewer's character has ground line of sight to another character, and whether that character is seen
 * through a building (ADR-040's amendment of 2026-10-06 for P1.9-T116 and P1.9-T117): a character hidden by buildings
 * the viewer's page has not faded is drawn as a silhouette, with its world UI, while the viewer's character has a clear
 * straight line over the ground to it. The line is combat's ray (`inSight`, ADR-073 decision 8) over the page's live
 * collision grid between the centres of the two feet boxes, so `SOLID` cells block it and water and holes do not.
 * Cosmetic only: the page holds every character it judges. Nothing here allocates.
 */

/**
 * The knobs of "seen through a building": the share of a character's body the buildings drawn above it must hide
 * together, and how long the answer outlives the ground line of sight, in ms, so a ray grazing a corner cell does not
 * flicker.
 */
export interface SeenThroughRule {
  readonly coverage: number;
  readonly holdMs: number;
}

/**
 * Whether two characters have ground line of sight: combat's ray between the centres of their feet boxes, walked from
 * the end with the smaller y, then the smaller x, so the answer is the same whichever of the two asks.
 *
 * @param grid The page's live collision grid: the compiled cells with every thing's footprint stamped in.
 * @param ax One character's feet x in whole sixteenths.
 * @param ay Its feet line.
 * @param bx The other character's feet x in whole sixteenths.
 * @param by Its feet line.
 * @returns True when no `SOLID` cell lies between the two feet boxes' centres.
 * @throws RangeError when a coordinate is not a whole number.
 */
export function groundSight(grid: CollisionGrid, ax: number, ay: number, bx: number, by: number): boolean {
  if (!Number.isInteger(ax) || !Number.isInteger(ay) || !Number.isInteger(bx) || !Number.isInteger(by)) {
    throw new RangeError(`ground sight takes feet points in whole sixteenths, received (${ax}, ${ay}) and (${bx}, ${by})`);
  }
  if (ay < by || (ay === by && ax <= bx)) {
    return inSight(grid, ax, ay - ORIGIN_RISE, bx, by - ORIGIN_RISE);
  }
  return inSight(grid, bx, by - ORIGIN_RISE, ax, ay - ORIGIN_RISE);
}

/**
 * The time until which a character once seen through a building stays so without ground line of sight: moved to the
 * rule's hold past now on every judgement made in ground sight, and left as it was on one made without. The caller
 * keeps the answer with the character and hands it to `seenThroughNext`.
 *
 * @param grounded Whether the viewer's character has ground line of sight to it now (`groundSight`).
 * @param heldUntilMs The time kept from its last judgement, in ms.
 * @param nowMs The time of this judgement, on the same clock.
 * @param rule The knobs.
 * @returns The time to keep.
 */
export function groundSightHeldUntil(grounded: boolean, heldUntilMs: number, nowMs: number, rule: SeenThroughRule): number {
  return grounded ? nowMs + rule.holdMs : heldUntilMs;
}

/**
 * Whether a character is seen through a building now: the buildings not faded on the viewer's page hide the rule's
 * share of its body together, it is not roofed apart, no closed roof hides any of its body, and the viewer's
 * character has ground line of sight to it, or had it within the hold while the character was seen through. A
 * character behind a building the viewer's page has faded is handed a union of 0 and is never seen through: it is
 * seen through the fade itself.
 *
 * @param was Whether it was seen through at its last judgement.
 * @param heldUntilMs The time the hold lasts until (`groundSightHeldUntil`), in ms.
 * @param keptUnion The share of its body box the buildings drawn above it at full alpha hide together, in `[0, 1]`.
 * @param roofed Whether it stands under a roof the viewer is not under (`roofedApart`).
 * @param roofShare The share of its body box a closed roof drawn above it hides, in `[0, 1]`.
 * @param grounded Whether the viewer's character has ground line of sight to it now (`groundSight`).
 * @param nowMs The time of this judgement, on the hold's clock.
 * @param rule The knobs.
 * @returns True when it is drawn as a silhouette and keeps its world UI.
 */
export function seenThroughNext(was: boolean, heldUntilMs: number, keptUnion: number, roofed: boolean, roofShare: number, grounded: boolean, nowMs: number, rule: SeenThroughRule): boolean {
  if (roofed || roofShare > 0 || !(keptUnion > 0 && keptUnion >= rule.coverage)) {
    return false;
  }
  return grounded || (was && nowMs <= heldUntilMs);
}
